import React, { useMemo, useState, useEffect } from "react";
import { resolveUrl } from "@/components/ImageUploader";
import { Check, ShoppingBag, Sparkles, Info } from "lucide-react";

const fmtEuro = (n) => "€ " + (Number(n) || 0).toFixed(2).replace(".", ",");

const ICONS = {
  tessuti: "🧶", gambe: "🦵", piedi: "🦶", metalli: "✨", piani: "🟫", basi: "🦴",
  finiture: "🎨", maniglie: "🔘", dimensioni: "📐", ante: "🚪",
  configurazioni: "🛋️", giroletti: "🛏️", paralumi: "💡", cornici: "🖼️",
};
const LABELS = {
  tessuti: "Tessuto", gambe: "Gamba", piedi: "Piede", metalli: "Metallo",
  piani: "Piano", basi: "Base", finiture: "Finitura", maniglie: "Maniglia",
  dimensioni: "Misura", ante: "Ante", configurazioni: "Configurazione",
  giroletti: "Giroletto", paralumi: "Paralume", cornici: "Cornice",
};

/**
 * VisualConfigurator: configuratore visivo per la pagina prodotto B2C.
 *
 * - Mostra una "stage" con la foto prodotto + le scelte attuali sovrapposte
 * - Per ogni asse del configuratore, lista di pill cliccabili con swatch/immagine
 * - Calcolo prezzo live: prezzo_base + Σ(sovrapprezzo opzione selezionata)
 * - SKU runtime: SKU_MADRE-<TESSUTO>-<GAMBA>-<METALLO>
 * - onComposition: callback con { selezioni, prezzoTotale, skuRuntime }
 */
export default function VisualConfigurator({ prodotto, options, prezzoBase, productImage, accentColor = "#C25934", onComposition }) {
  const axes = Object.keys(options || {}).filter((k) => (options[k] || []).length > 0);
  const [selections, setSelections] = useState({});

  // Pre-seleziona la prima opzione di ogni asse
  useEffect(() => {
    const initial = {};
    axes.forEach((ax) => {
      if (options[ax]?.length > 0) initial[ax] = options[ax][0].codice;
    });
    setSelections(initial);
  }, [JSON.stringify(options)]); // eslint-disable-line react-hooks/exhaustive-deps

  const itemForAxis = (axis) => (options[axis] || []).find((it) => it.codice === selections[axis]);

  const surcharge = useMemo(() => {
    let s = 0;
    axes.forEach((ax) => {
      const item = itemForAxis(ax);
      if (item) s += Number(item.sovrapprezzo || 0);
    });
    return s;
  }, [selections, options]); // eslint-disable-line react-hooks/exhaustive-deps

  const prezzoTotale = (Number(prezzoBase) || 0) + surcharge;

  // SKU runtime generato dalle scelte
  const skuRuntime = useMemo(() => {
    const parts = [prodotto?.sku_madre || prodotto?.sku_variante || "PROD"];
    axes.forEach((ax) => { if (selections[ax]) parts.push(selections[ax]); });
    return parts.join("-");
  }, [selections, axes, prodotto]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onComposition?.({ selezioni: selections, prezzoTotale, surcharge, skuRuntime });
  }, [selections, prezzoTotale, surcharge, skuRuntime]); // eslint-disable-line react-hooks/exhaustive-deps

  if (axes.length === 0) return null;

  const tessutoSel = itemForAxis("tessuti");
  const gambaSel = itemForAxis("gambe") || itemForAxis("piedi");
  const metalloSel = itemForAxis("metalli");

  return (
    <div className="bg-neutral-50 border border-border rounded-sm overflow-hidden" data-testid="visual-configurator">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-white">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" style={{ color: accentColor }} />
          <div className="font-heading font-bold text-sm">Personalizza il tuo prodotto</div>
        </div>
        <div className="text-[10px] tracking-wider uppercase text-muted-foreground">
          Lead time {prodotto?.lead_time_configurazione_gg || 60}-{(prodotto?.lead_time_configurazione_gg || 60) + 30} gg
        </div>
      </div>

      {/* Stage: foto prodotto + composizione live in overlay */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-neutral-100 to-neutral-200 overflow-hidden" data-testid="composition-stage">
        {productImage ? (
          <img src={resolveUrl(productImage)} alt={prodotto?.nome}
            className="w-full h-full object-contain transition-all duration-500"
            style={{
              filter: metalloSel?.hex_color ? `drop-shadow(0 0 0 ${metalloSel.hex_color})` : "none",
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">Nessuna immagine prodotto</div>
        )}

        {/* Overlay: scheda composizione in basso a sinistra */}
        <div className="absolute bottom-3 left-3 right-3 md:right-auto md:max-w-xs bg-white/95 backdrop-blur-sm border border-border rounded-sm p-3 shadow-lg" data-testid="composition-card">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">La tua scelta</div>
          <div className="flex items-center gap-2 mb-2">
            {tessutoSel?.immagine_url ? (
              <img src={resolveUrl(tessutoSel.immagine_url)} alt="" className="w-10 h-10 rounded-sm object-cover border border-border" />
            ) : tessutoSel?.hex_color ? (
              <div className="w-10 h-10 rounded-sm border border-border" style={{ background: tessutoSel.hex_color }} />
            ) : tessutoSel ? (
              <div className="w-10 h-10 rounded-sm bg-amber-100 border border-border flex items-center justify-center text-[9px]">{tessutoSel.codice?.slice(0,3)}</div>
            ) : null}
            {gambaSel?.immagine_url ? (
              <img src={resolveUrl(gambaSel.immagine_url)} alt="" className="w-10 h-10 rounded-sm object-cover border border-border" />
            ) : gambaSel ? (
              <div className="w-10 h-10 rounded-sm bg-neutral-700 border border-border flex items-center justify-center text-[9px] text-white">{gambaSel.codice?.slice(0,4)}</div>
            ) : null}
            {metalloSel?.hex_color && (
              <div className="w-10 h-10 rounded-full border-2 border-white shadow-md" style={{ background: metalloSel.hex_color }} title={metalloSel.nome} />
            )}
          </div>
          <div className="text-xs space-y-0.5">
            {tessutoSel && <div className="truncate"><span className="text-muted-foreground">Tessuto:</span> <strong>{tessutoSel.nome}</strong> · {tessutoSel.nome_colore || tessutoSel.codice_colore}</div>}
            {gambaSel && <div className="truncate"><span className="text-muted-foreground">{LABELS[axes.find((a) => a === "gambe") ? "gambe" : "piedi"]}:</span> <strong>{gambaSel.nome}</strong></div>}
            {metalloSel && <div className="truncate"><span className="text-muted-foreground">Metallo:</span> <strong>{metalloSel.nome}</strong></div>}
          </div>
        </div>
      </div>

      {/* Selettori per ogni asse */}
      <div className="p-5 space-y-5">
        {axes.map((axis) => {
          const items = options[axis] || [];
          const selectedCode = selections[axis];
          return (
            <div key={axis} data-testid={`axis-${axis}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{ICONS[axis] || "🔧"}</span>
                  <div className="font-semibold text-sm">{LABELS[axis] || axis}</div>
                  {selectedCode && (
                    <span className="text-xs text-muted-foreground">· {itemForAxis(axis)?.nome || selectedCode}</span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">{items.length} opzioni</div>
              </div>

              {/* Per metalli: pill colorate; per tessuti: swatch piccoli; per gambe: card più grandi */}
              <div className={`grid gap-2 ${axis === "metalli" ? "grid-cols-5 sm:grid-cols-10" : axis === "tessuti" ? "grid-cols-4 sm:grid-cols-6" : "grid-cols-3 sm:grid-cols-5"}`}>
                {items.map((it) => {
                  const isSel = it.codice === selectedCode;
                  return (
                    <button key={it.id || it.codice}
                      onClick={() => setSelections((s) => ({ ...s, [axis]: it.codice }))}
                      data-testid={`opt-${axis}-${it.codice}`}
                      title={`${it.nome}${it.sovrapprezzo > 0 ? " · +" + fmtEuro(it.sovrapprezzo) : ""}`}
                      className={`relative aspect-square rounded-sm overflow-hidden border-2 transition-all hover:scale-105 ${isSel ? "ring-2 ring-offset-2 shadow-lg" : "border-transparent opacity-90 hover:opacity-100"}`}
                      style={{ borderColor: isSel ? accentColor : "transparent", ringColor: accentColor }}>
                      {it.immagine_url ? (
                        <img src={resolveUrl(it.immagine_url)} alt={it.nome} className="w-full h-full object-cover" />
                      ) : it.hex_color ? (
                        <div className="w-full h-full" style={{ background: it.hex_color }} />
                      ) : (
                        <div className="w-full h-full bg-accent/30 flex items-center justify-center text-[10px] text-center px-1 leading-tight">
                          {it.codice_colore || it.codice?.slice(0, 6)}
                        </div>
                      )}
                      {isSel && (
                        <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full flex items-center justify-center" style={{ background: accentColor }}>
                          <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                        </div>
                      )}
                      {Number(it.sovrapprezzo || 0) > 0 && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[9px] py-0.5 text-center font-medium">
                          +{fmtEuro(it.sovrapprezzo)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Riepilogo prezzo live */}
      <div className="bg-foreground text-white p-5" data-testid="live-summary">
        <div className="overline mb-3" style={{ color: accentColor }}>Riepilogo configurazione</div>
        <div className="space-y-1 text-sm mb-3">
          <SummaryRow label="Prezzo base" value={fmtEuro(prezzoBase)} />
          {axes.map((axis) => {
            const item = itemForAxis(axis);
            if (!item || !Number(item.sovrapprezzo)) return null;
            return <SummaryRow key={axis} label={`${LABELS[axis]} ${item.codice}`} value={`+ ${fmtEuro(item.sovrapprezzo)}`} muted />;
          })}
        </div>
        <div className="flex items-baseline justify-between border-t border-white/15 pt-3">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-white/50">Totale personalizzato</div>
            <div className="font-mono text-[10px] text-white/40 truncate max-w-xs">{skuRuntime}</div>
          </div>
          <div className="font-heading font-bold text-3xl" data-testid="live-price" style={{ color: accentColor }}>
            {fmtEuro(prezzoTotale)}
          </div>
        </div>
        <div className="mt-3 text-[11px] text-white/50 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span>Prodotto su misura · consegna stimata {prodotto?.lead_time_configurazione_gg || 60}-{(prodotto?.lead_time_configurazione_gg || 60) + 30} gg lavorativi</span>
        </div>
      </div>
    </div>
  );
}

const SummaryRow = ({ label, value, muted }) => (
  <div className={`flex justify-between ${muted ? "text-white/70" : ""}`}>
    <span>{label}</span>
    <span className="font-medium tabular-nums">{value}</span>
  </div>
);
